import type { Media, MediaKind, MediaStatus } from '@/types/api'

/* ------------------------------------------------------------------ kinds */

export const KIND_LABELS: Record<MediaKind, string> = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  svg: 'SVG',
  pdf: 'PDF',
  html_package: 'HTML',
  font: 'Font',
}

/** Plural labels for filter chips. */
export const KIND_FILTER_LABELS: Record<MediaKind, string> = {
  image: 'Images',
  video: 'Videos',
  audio: 'Audio',
  svg: 'SVG',
  pdf: 'PDF',
  html_package: 'HTML',
  font: 'Fonts',
}

export function kindLabel(kind: MediaKind | string): string {
  return KIND_LABELS[kind as MediaKind] ?? kind
}

/** Kinds whose thumbnails are best shown uncropped on a checkerboard (transparency, vector). */
export function isContainKind(kind: MediaKind): boolean {
  return kind === 'svg' || kind === 'font' || kind === 'pdf'
}

export function hasTimeline(kind: MediaKind): boolean {
  return kind === 'video' || kind === 'audio'
}

/* ------------------------------------------------------------------ status */

export type MediaTone = 'success' | 'info' | 'warning' | 'danger' | 'muted'

export interface StatusDisplay {
  label: string
  tone: MediaTone
  /** Still moving towards a terminal state (spinner / shimmer). */
  busy: boolean
  /** ready | failed | rejected */
  terminal: boolean
  /** Can't be used in content. */
  broken: boolean
}

const TERMINAL: ReadonlySet<MediaStatus> = new Set(['ready', 'failed', 'rejected'])

export function isTerminalStatus(status: MediaStatus | string): boolean {
  return TERMINAL.has(status as MediaStatus)
}

export function mediaStatusDisplay(status: MediaStatus | string): StatusDisplay {
  switch (status) {
    case 'ready':
      return { label: 'Ready', tone: 'success', busy: false, terminal: true, broken: false }
    case 'uploading':
      return { label: 'Uploading', tone: 'info', busy: true, terminal: false, broken: false }
    case 'processing':
      return { label: 'Processing', tone: 'info', busy: true, terminal: false, broken: false }
    case 'failed':
      return { label: 'Failed', tone: 'danger', busy: false, terminal: true, broken: true }
    case 'rejected':
      return { label: 'Rejected', tone: 'warning', busy: false, terminal: true, broken: true }
    default:
      return { label: String(status), tone: 'muted', busy: false, terminal: false, broken: false }
  }
}

const FAILURE_TEXT: Record<string, string> = {
  duplicate: 'Already in your library',
  unsupported_type: 'File type isn’t supported',
  mime_mismatch: 'File contents don’t match its type',
  size_mismatch: 'Upload was incomplete',
  corrupt: 'File is damaged or unreadable',
  svg_unsafe: 'SVG couldn’t be made safe',
  encrypted: 'Password-protected PDFs aren’t supported',
  too_many_pages: 'PDF has too many pages (max 200)',
  too_large: 'File is too large',
  too_many_pixels: 'Image is too large (max 50 megapixels)',
  too_many_frames: 'GIF has too many frames (max 300)',
  too_long: 'Media is too long (max 4 hours)',
  resolution_too_high: 'Resolution is too high (max 8K)',
  kind_mismatch: 'Replacement must be the same type of file',
  package_invalid: 'HTML package is invalid',
  package_unsafe: 'HTML package contains unsafe files',
  processing_failed: 'Processing failed — try again',
  quota: 'Not enough storage',
  quota_exceeded: 'Not enough storage',
  timeout: 'Processing took too long',
  processing_error: 'Processing failed — try again',
  tool_error: 'Processing failed — try again',
  upload_expired: 'Upload expired',
}

/** Human text for a `failure_code`; falls back to the server detail, then a humanised code. */
export function failureText(code: string | null | undefined, detail?: string | null): string {
  if (code && FAILURE_TEXT[code]) return FAILURE_TEXT[code]
  if (detail) return detail
  if (code) {
    const t = code.replace(/[_-]+/g, ' ').trim()
    return t.charAt(0).toUpperCase() + t.slice(1)
  }
  return 'Processing failed'
}

export function mediaFailureText(media: Pick<Media, 'status' | 'failure_code' | 'failure_detail'>): string | null {
  if (media.status !== 'failed' && media.status !== 'rejected') return null
  return failureText(media.failure_code, media.failure_detail)
}

/* -------------------------------------------------------------- formatting */

/** `65_000` → `1:05`, `3_723_000` → `1:02:03`. Sub-second clips round up to `0:01`. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—'
  let total = Math.round(ms / 1000)
  if (total === 0 && ms > 0) total = 1
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

export function formatDimensions(media: Pick<Media, 'width' | 'height'>): string | null {
  return media.width && media.height ? `${media.width} × ${media.height}` : null
}

/** `5_200_000` → `5.2 Mbps`. */
export function formatBitrate(bps: number | null | undefined): string {
  if (!bps || bps <= 0) return '—'
  if (bps >= 1_000_000) return `${Number((bps / 1_000_000).toFixed(1))} Mbps`
  if (bps >= 1000) return `${Math.round(bps / 1000)} kbps`
  return `${bps} bps`
}

export function formatFps(fps: number | null | undefined): string {
  if (!fps) return '—'
  return `${Number(fps.toFixed(fps % 1 === 0 ? 0 : 2))} fps`
}

/** One-line technical summary for list rows: dimensions, duration or pages. */
export function mediaSpec(media: Media): string | null {
  const parts: string[] = []
  const dims = formatDimensions(media)
  if (dims && media.kind !== 'audio') parts.push(dims)
  if (hasTimeline(media.kind) && media.duration_ms) parts.push(formatDuration(media.duration_ms))
  if (media.kind === 'pdf' && media.page_count) parts.push(`${media.page_count} page${media.page_count === 1 ? '' : 's'}`)
  return parts.length ? parts.join(' · ') : null
}

export function shortHash(sha: string | null | undefined, size = 12): string {
  return sha ? sha.slice(0, size) : '—'
}

/** Days until `purge_after` (trash), floored at 0. */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.ceil((t - now.getTime()) / 86_400_000))
}

/* --------------------------------------------------- datetime-local inputs */

const pad = (n: number) => String(n).padStart(2, '0')

/** ISO → `YYYY-MM-DDTHH:mm` in local time for `<input type="datetime-local">`. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** `datetime-local` value → ISO-8601 UTC, or null when empty/invalid. */
export function fromLocalInput(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/* -------------------------------------------------- client-side file typing */

const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heic',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  mp3: 'audio/mpeg',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  wav: 'audio/wav',
  pdf: 'application/pdf',
  zip: 'application/zip',
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
}

/** `accept` attribute for the file picker. */
export const UPLOAD_ACCEPT = [
  'image/*',
  'video/*',
  'audio/*',
  'application/pdf',
  'application/zip',
  ...Object.keys(EXT_MIME).map((e) => `.${e}`),
].join(',')

/** `accept` for replacing a media of `kind` (same kind only). */
export function acceptForKind(kind: MediaKind): string {
  const exts = Object.entries(EXT_MIME)
    .filter(([, mime]) => kindForMime(mime) === kind)
    .map(([ext]) => `.${ext}`)
  const mimes = kind === 'image' ? ['image/*'] : kind === 'video' ? ['video/*'] : kind === 'audio' ? ['audio/*'] : []
  return [...mimes, ...exts].join(',')
}

export function fileExtension(name: string): string {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(i + 1).toLowerCase() : ''
}

/** Declared MIME for the intent: browser type, else by extension. The server sniffs the real type. */
export function mimeForFile(file: { name: string; type: string }): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type
  return EXT_MIME[fileExtension(file.name)] ?? 'application/octet-stream'
}

export function kindForMime(mime: string): MediaKind | null {
  const m = mime.toLowerCase()
  if (m === 'image/svg+xml') return 'svg'
  if (m.startsWith('image/')) return 'image'
  if (m.startsWith('video/')) return 'video'
  if (m.startsWith('audio/')) return 'audio'
  if (m === 'application/pdf') return 'pdf'
  if (m === 'application/zip' || m === 'application/x-zip-compressed') return 'html_package'
  if (m.startsWith('font/') || m === 'application/font-woff' || m === 'application/x-font-ttf' || m === 'application/x-font-otf') return 'font'
  return null
}

/** Best-effort kind of a local file (advisory — used for icons and "replace must keep the kind"). */
export function kindForFile(file: { name: string; type: string }): MediaKind | null {
  return kindForMime(mimeForFile(file)) ?? kindForMime(EXT_MIME[fileExtension(file.name)] ?? '')
}

/** Display name the server will derive: filename without extension. */
export function baseName(filename: string): string {
  const i = filename.lastIndexOf('.')
  return i > 0 ? filename.slice(0, i) : filename
}
